import { HttpErrorResponse } from '@angular/common/http';
import { ChangeDetectionStrategy, Component, DestroyRef, computed, inject, input, output, signal } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { MatDialog } from '@angular/material/dialog';
import { MatIconModule } from '@angular/material/icon';
import { MatTooltipModule } from '@angular/material/tooltip';
import { EMPTY, catchError, finalize } from 'rxjs';
import { GboxScansApiService } from '@/tms/gbox-scans/_api/gbox-scans.api.service';
import { ScanLedgerFormComponent } from '@/tms/gbox-scans/_components/scan-ledger-form/scan-ledger-form.component';
import { ScanStatusBadgesComponent } from '@/tms/gbox-scans/_components/scan-status-badges/scan-status-badges.component';
import { CostStatus, Scan, ScanDialogResult } from '@/tms/gbox-scans/_models/gbox-scans.model';
import { ScanPreviewDialogComponent } from '@/tms/box-list/scan-preview-dialog/scan-preview-dialog.component';
import { GboxAnswer, ScanAnswerRef } from '@/tms/reports/_models/reports.model';
import { IsScanAnswerPipe } from '@/tms/reports/_pipes/is-scan-answer.pipe';
import { QuestionLabelPipe } from '@/tms/reports/_pipes/question-label.pipe';
import { extractScanCostHints } from '@/tms/reports/_utils/extract-scan-cost-hints.util';

interface DecisionOption {
	value: CostStatus;
	label: string;
	icon: string;
}

@Component({
	selector: 'app-report-answers-table',
	standalone: true,
	changeDetection: ChangeDetectionStrategy.OnPush,
	imports: [
		MatIconModule,
		MatTooltipModule,
		IsScanAnswerPipe,
		QuestionLabelPipe,
		ScanStatusBadgesComponent,
		ScanLedgerFormComponent,
	],
	templateUrl: './report-answers-table.component.html',
	styleUrl: './report-answers-table.component.scss',
})
export class ReportAnswersTableComponent {
	readonly answers = input<GboxAnswer[] | null>(null);
	readonly driverId = input<string | null>(null);
	readonly scanUpdated = output<Scan>();

	readonly decisionOptions: ReadonlyArray<DecisionOption> = [
		{ value: 'pending', label: 'Oczekuje', icon: 'fa-hourglass-half' },
		{ value: 'confirmed', label: 'Zatwierdź', icon: 'fa-check' },
		{ value: 'rejected', label: 'Odrzuć', icon: 'fa-xmark' },
		{ value: 'skipped', label: 'Pomiń', icon: 'fa-forward' },
	];

	readonly previewLoadingId = signal<number | null>(null);
	readonly busyScanId = signal<number | null>(null);
	readonly scanErrors = signal<Readonly<Record<number, string>>>({});
	readonly openFormScan = signal<Scan | null>(null);

	readonly orderedAnswers = computed<GboxAnswer[]>(() => {
		const list = this.answers() ?? [];
		const questions = list.filter((answer) => !this.isScanAnswer(answer));
		const scans = list.filter((answer) => this.isScanAnswer(answer));
		return [...questions, ...scans];
	});

	readonly costHints = computed(() => extractScanCostHints(this.answers()));

	readonly decisionScans = computed<ScanAnswerRef[]>(() => {
		const seen = new Set<number>();
		const scans: ScanAnswerRef[] = [];
		for (const answer of this.answers() ?? []) {
			if (answer.scan && !seen.has(answer.scan.id)) {
				seen.add(answer.scan.id);
				scans.push(answer.scan);
			}
		}
		return scans;
	});

	private readonly scanApi = inject(GboxScansApiService);
	private readonly dialog = inject(MatDialog);
	private readonly destroyRef = inject(DestroyRef);

	openScanPreview(scan: ScanAnswerRef): void {
		if (this.previewLoadingId() !== null) {
			return;
		}
		this.previewLoadingId.set(scan.id);
		this.scanApi
			.get(scan.id)
			.pipe(
				catchError(() => EMPTY),
				finalize(() => this.previewLoadingId.set(null)),
				takeUntilDestroyed(this.destroyRef),
			)
			.subscribe(({ data }) => this.openScanDialog(data));
	}

	setDecision(scan: ScanAnswerRef, status: CostStatus): void {
		if (this.busyScanId() !== null || scan.costStatus === status) {
			return;
		}
		if (status === 'confirmed') {
			this.openLedgerForm(scan);
			return;
		}
		this.clearScanError(scan.id);
		this.busyScanId.set(scan.id);
		this.scanApi
			.update(scan.id, { costStatus: status, updatedAt: scan.updatedAt, force: false })
			.pipe(
				catchError((err: unknown) => {
					this.setScanError(scan.id, this.resolveDecisionError(err));
					return EMPTY;
				}),
				finalize(() => this.busyScanId.set(null)),
				takeUntilDestroyed(this.destroyRef),
			)
			.subscribe((res) => this.scanUpdated.emit(res.data));
	}

	openLedgerForm(scan: ScanAnswerRef): void {
		if (this.busyScanId() !== null || this.openFormScan()?.id === scan.id) {
			return;
		}
		this.clearScanError(scan.id);
		this.busyScanId.set(scan.id);
		this.scanApi
			.get(scan.id)
			.pipe(
				catchError(() => {
					this.setScanError(scan.id, 'Nie udało się pobrać danych skanu.');
					return EMPTY;
				}),
				finalize(() => this.busyScanId.set(null)),
				takeUntilDestroyed(this.destroyRef),
			)
			.subscribe(({ data }) => this.openFormScan.set(data));
	}

	onLedgerPosted(scan: Scan): void {
		this.openFormScan.set(null);
		this.scanUpdated.emit(scan);
	}

	onLedgerCancelled(): void {
		this.openFormScan.set(null);
	}

	private isScanAnswer(answer: GboxAnswer): boolean {
		return answer.scan != null || /^scan-\d+$/.test((answer.answer ?? '').trim());
	}

	private openScanDialog(scan: Scan): void {
		this.dialog
			.open<ScanPreviewDialogComponent, unknown, ScanDialogResult | undefined>(ScanPreviewDialogComponent, {
				data: { boxId: scan.idBox, scanId: scan.id, name: scan.name, scan },
				width: '90vw',
				maxWidth: '1280px',
				autoFocus: false,
			})
			.afterClosed()
			.pipe(takeUntilDestroyed(this.destroyRef))
			.subscribe((result) => {
				if (result) {
					this.scanUpdated.emit(result.updated);
				}
			});
	}

	private resolveDecisionError(err: unknown): string {
		if (err instanceof HttpErrorResponse) {
			if (err.status === 409) {
				return 'Skan został zmieniony w innym miejscu — odśwież widok i spróbuj ponownie.';
			}
			const body = err.error;
			if (typeof body === 'object' && body !== null) {
				const record = body as Record<string, unknown>;
				if (typeof record['message'] === 'string') {
					return record['message'];
				}
				if (typeof record['detail'] === 'string') {
					return record['detail'];
				}
			}
		}
		return 'Nie udało się zmienić statusu.';
	}

	private setScanError(scanId: number, message: string): void {
		this.scanErrors.update((errors) => ({ ...errors, [scanId]: message }));
	}

	private clearScanError(scanId: number): void {
		this.scanErrors.update((errors) => {
			if (!(scanId in errors)) {
				return errors;
			}
			const next = { ...errors };
			delete next[scanId];
			return next;
		});
	}
}
